/**
 * RevokeApiKeyButton Component
 *
 * Button to revoke an API key with confirmation dialog.
 */
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { Button, Icon } from '@insio/ui-toolkit';
import { RevokeApiKeyDocument } from '@/graphql';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';

interface RevokeApiKeyButtonProps {
  keyId: string;
  onRevoked?: () => void;
}

export function RevokeApiKeyButton({ keyId, onRevoked }: RevokeApiKeyButtonProps) {
  const t = useFeatureTranslation('systemSettings.apiKeys');
  const { showSuccess, showError } = useToastHelpers();
  const [showConfirm, setShowConfirm] = useState(false);
  const [revokeApiKey, { loading, error }] = useMutation(RevokeApiKeyDocument);

  const handleRevoke = async () => {
    try {
      await revokeApiKey({
        variables: { id: keyId }
      });
      setShowConfirm(false);
      onRevoked?.();
      showSuccess(t('success.revokeSuccess'));
    } catch {
      const errorMessage = error?.message || t('errors.revokeError');
      showError(errorMessage);
    }
  };

  return (
    <div className='flex justify-center'>
      {!showConfirm ? (
        <Button
          variant='ghost'
          size='sm'
          onClick={() => setShowConfirm(true)}
          aria-label={t('actions.revoke')}
          disabled={loading}
          testId={`api-key-revoke-btn-${keyId}`}
        >
          <Icon name='trash' size='sm' />
        </Button>
      ) : (
        <div className='flex items-center justify-center gap-xs'>
          <Button
            variant='ghost'
            size='sm'
            onClick={handleRevoke}
            disabled={loading}
            aria-label={t('actions.confirmRevoke')}
            testId={`api-key-revoke-confirm-btn-${keyId}`}
          >
            <Icon name='check' size='sm' />
          </Button>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => setShowConfirm(false)}
            disabled={loading}
            aria-label={t('actions.cancel')}
            testId={`api-key-revoke-cancel-btn-${keyId}`}
          >
            <Icon name='xmark' size='sm' />
          </Button>
        </div>
      )}
    </div>
  );
}
